import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { handlePostRequest } from '../../utils/api-handler.util.js'
import { showToaster } from '../../utils/show-toaster.util.js'
import { useAuth } from '../../contexts/AuthContext'
import { QueryKeys } from '../../utils/queryKeyGenerator.js'
import InputField from '../common/InputField.jsx'
import SubmittedMessage from './SubmittedMessage.jsx'
import useProductCategories from '../../hooks/useProductCategories.js'
import DisplayForUserType from '../auth/DisplayForUserType.jsx'
import USER_TYPE from '../../constants/userType.js'

const SelectedProductForm = ({ selectedProduct, onBack, onSuccess }) => {
  const { userType } = useAuth()
  const { getCategoryNameById } = useProductCategories()
  const [sellingPrice, setSellingPrice] = useState(selectedProduct.price)
  const [minOrderQuantity, setMinOrderQuantity] = useState(selectedProduct.minOrderQuantity || 1)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [newProductSlug, setNewProductSlug] = useState(null)

  const retailerProductMutation = useMutation({
    mutationFn: (formData) => handlePostRequest(`/${userType}s/my-products`, formData),
    onSuccess: (data) => {
      showToaster('success', 'Product added successfully')
      QueryKeys.listShopProducts.invalidate(userType)
      QueryKeys.availableProducts.invalidateAll()

      const createdProductSlug = data?.data?.productSlug
      setIsSubmitted(true)

      if (createdProductSlug) {
        setNewProductSlug(createdProductSlug)
      }

      if (onSuccess) {
        onSuccess(data)
      }
    },
  })

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      await retailerProductMutation.mutateAsync({
        productId: selectedProduct.id,
        price: sellingPrice,
        minOrderQuantity: minOrderQuantity,
      })
    } catch (error) {
      showToaster('error', 'Failed to add product. Please try again.')
    }
  }

  const isBusy = retailerProductMutation.isLoading

  return isSubmitted ? (
    <div className="fixed inset-0 bg-background z-50 flex items-center justify-center">
      <SubmittedMessage
        message="Product added to your shop"
        buttonMessage="View Product"
        redirectPath={`/${userType}/products/${newProductSlug}`}
      />
    </div>
  ) : (
    <div className="">
      <div className="bg-background border border-gray-200 rounded-xl p-6 mb-1 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="relative">
            <img
              src={selectedProduct.primaryImageUrl}
              alt={selectedProduct.name}
              className="w-24 h-24 object-cover rounded-lg shadow-md"
            />
            <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium">
              Selected
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-gray-900 text-lg truncate">{selectedProduct.name}</h4>
            <div className="mt-1 space-y-1">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                  {getCategoryNameById(selectedProduct.categoryId) || 'Uncategorized'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {selectedProduct.description && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-700 leading-relaxed">{selectedProduct.description}</p>
          </div>
        )}
      </div>

      <div className="text-left mb-6">
        <button
          className="text-sm text-secondary-600 underline hover:text-orange-600 transition-colors"
          onClick={() => onBack()}
        >
          remove selection
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <fieldset disabled={isBusy} className="space-y-4">
          <div>
            <InputField
              id="sellingPrice"
              name="sellingPrice"
              type="number"
              value={Number(sellingPrice)}
              onChange={(e) => setSellingPrice(e.target.value)}
              required
              placeholder="Enter your selling price"
              label="Your Selling Price (Ksh)"
              min="0"
            />
            <p className="text-xs text-gray-500 mt-1">
              Set the price at which you want to sell this product in your shop
            </p>
          </div>
          <DisplayForUserType userTypes={[USER_TYPE.wholeseller]}>
            <div className="mt-4">
              <InputField
                id="minOrderQuantity"
                name="minOrderQuantity"
                type="number"
                value={Number(minOrderQuantity)}
                onChange={(e) => setMinOrderQuantity(e.target.value)}
                required
                placeholder="Enter minimum order quantity"
                label="Minimum Order Quantity"
                min="1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Set the minimum quantity required to purchase this product
              </p>
            </div>
          </DisplayForUserType>

          <div className="pt-4">
            <button
              type="submit"
              className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-secondary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 ${
                isBusy ? 'opacity-70 cursor-wait' : ''
              }`}
              disabled={isBusy}
            >
              {isBusy ? 'Adding Product...' : 'Add to My Shop'}
            </button>
          </div>
        </fieldset>
      </form>
    </div>
  )
}

export default SelectedProductForm
